import { useAuthProvider, useDataProvider } from "ra-core";
import { createContext, useContext, useMemo, type ReactNode } from "react";
import { getSupabaseClient } from "@/components/atomic-crm/providers/supabase/supabase";
import { IS_DEMO } from "../../env";
import { createLocalDataQualityClient } from "./localDataQualityClient";
import {
  createSupabaseDataQualityClient,
  type DqRpcClient,
} from "./supabaseDataQualityClient";
import type { DataQualityClient } from "./types";

const DataQualityClientContext = createContext<DataQualityClient | null>(null);

/** Tests (and any host) can inject a client; otherwise RPCs on Supabase, local computation in the demo. */
export const DataQualityClientProvider = ({
  client,
  children,
}: {
  client: DataQualityClient;
  children: ReactNode;
}) => (
  <DataQualityClientContext.Provider value={client}>
    {children}
  </DataQualityClientContext.Provider>
);

export const useDataQualityClient = (): DataQualityClient => {
  const injected = useContext(DataQualityClientContext);
  const dataProvider = useDataProvider();
  const authProvider = useAuthProvider();
  return useMemo(
    () =>
      injected ??
      (IS_DEMO
        ? createLocalDataQualityClient(dataProvider, {
            isAdmin: async () =>
              (await authProvider?.getPermissions?.({})) === "admin",
          })
        : createSupabaseDataQualityClient(
            getSupabaseClient as unknown as () => DqRpcClient,
          )),
    [injected, dataProvider, authProvider],
  );
};
